import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Loader2, Search, Sparkles, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  createProspect,
  fetchProspectList,
  type ProspectRow,
} from "@/api/prospects";

/**
 * D-134 W2/W3 — «Потенційний клієнт»: пошук серед існуючих лідів (anti-dup) →
 * вибрати АБО inline-створити новий. Лід = Account зі cCOnboardingStatus='prospect'
 * (НЕ нова entity, НЕ Contact-сутність — контакт = поля). Existing-клієнти сюди НЕ
 * потрапляють (окремий holding-picker). onPick повертає prospect accountId + назву.
 */
export function ProspectPickerDrawer({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (accountId: string, name: string) => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [mode, setMode] = useState<"search" | "create">("search");

  // inline-create form (W3) — контакт = ПОЛЯ (НЕ Contact-сутність)
  const [name, setName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [comment, setComment] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ(""); setDebounced(""); setMode("search");
      setName(""); setContactPerson(""); setPhone(""); setEmail(""); setCity(""); setComment("");
      setErr(null); setCreating(false);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const listQ = useQuery({
    queryKey: ["prospect-picker", debounced],
    queryFn: ({ signal }) => fetchProspectList(debounced, signal),
    enabled: open && mode === "search",
    staleTime: 15_000,
  });

  const items: ProspectRow[] = useMemo(() => listQ.data?.items ?? [], [listQ.data]);

  const startCreate = () => {
    setName(q.trim());
    setErr(null);
    setMode("create");
  };

  const submitCreate = async () => {
    if (name.trim() === "") { setErr("Вкажіть назву потенційного клієнта"); return; }
    setCreating(true);
    setErr(null);
    try {
      const created = await createProspect({
        name: name.trim(),
        contactPerson: contactPerson.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        city: city.trim() || undefined,
        comment: comment.trim() || undefined,
      });
      onPick(created.id, created.name);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося створити ліда");
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[450px]"
      >
        <div className="sticky top-0 z-10 border-b bg-background px-5 py-4">
          <div className="flex items-center justify-between">
            <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
              <Sparkles className="h-4 w-4 text-primary" aria-hidden />
              {mode === "search" ? "Потенційний клієнт" : "Новий потенційний клієнт"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {mode === "search" ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                Знайдіть існуючого ліда або створіть нового. Без 1С — лід живе в CRM.
              </p>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Назва, телефон або email…"
                  className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
                />
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setMode("search"); setErr(null); }}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Назад до пошуку
            </button>
          )}
        </div>

        {mode === "search" ? (
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {listQ.isPending && debounced !== "" ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Пошук…
              </div>
            ) : (
              <>
                {/* anti-dup (W2/AC11): знайдені схожі ліди показані ПЕРЕД створенням */}
                {items.length > 0 && (
                  <>
                    {debounced !== "" && (
                      <p className="flex items-center gap-1.5 px-3 py-2 text-[11.5px] text-warning-foreground">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Схожі ліди вже є — оберіть існуючого, щоб не дублювати.
                      </p>
                    )}
                    <ul>
                      {items.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => onPick(p.id, p.name)}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left",
                              "hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                            )}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                              <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium">{p.name}</span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {[p.contactPerson, p.phone, p.city].filter(Boolean).join(" · ") || "лід"}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {debounced === "" && items.length === 0 && (
                  <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                    Почніть вводити назву потенційного клієнта.
                  </p>
                )}

                {/* live CTA — створити нового (0 dead-state, §0) */}
                <button
                  type="button"
                  onClick={startCreate}
                  className="mt-1 flex w-full items-center gap-3 rounded-lg border border-dashed border-primary/40 px-3 py-2.5 text-left text-primary hover:bg-primary/5"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <UserPlus className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="min-w-0 text-sm font-semibold">
                    {q.trim() ? `Створити нового «${q.trim()}»` : "Створити нового потенційного клієнта"}
                  </span>
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              <Field label="Назва" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="напр. Зоосвіт" />
              </Field>
              <Field label="Контактна особа">
                <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="напр. Іван Петренко" />
              </Field>
              <Field label="Телефон">
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+380…" />
              </Field>
              <Field label="Email">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" />
              </Field>
              <Field label="Місто / адреса">
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="напр. Київ" />
              </Field>
              <Field label="Коментар">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="нотатка про ліда" />
              </Field>

              {err && (
                <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {err}
                </p>
              )}
            </div>
            <div className="sticky bottom-0 flex gap-2 border-t bg-background px-5 py-4">
              <Button
                type="button"
                onClick={submitCreate}
                disabled={creating || name.trim() === ""}
                className="h-11 flex-1 text-sm font-semibold"
              >
                {creating ? (<><Loader2 className="h-4 w-4 animate-spin" /> Створення…</>) : "Створити й продовжити"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setMode("search")} className="h-11 text-sm">
                Скасувати
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
    </div>
  );
}
